/**
 * P12-PROD-PATH-R1-M1 — thin compatibility facade over the canonical transport authority.
 *
 * The extension-less `@/services/api-client` import resolves to this file (Vite resolves `.js`
 * before `.ts`). It delegates ALL HTTP transport to the canonical runtime-config client in
 * `./api-client.ts`. It owns NO transport concern: no URL construction, no `localhost`, no token
 * storage, no JWT/Authorization logging, no retry policy, no cache, and it does NOT import
 * `_deprecated_api-client.js`. Its only adaptation is the legacy
 * `apiClient.request(method, url, options)` signature used by a few pre-FE4 services, mapped onto
 * the canonical verb methods. Delete this file once a source audit proves zero importers.
 */
import canonicalApiClient, { ApiClient } from './api-client.ts'

export { ApiClient }

// `.request` supports BOTH the standard axios form `request(config)` and the legacy form
// `request(method, url, options)`. Both delegate to the canonical axios `request(config)`, so the
// FULL request config is preserved (timeout, responseType, withCredentials, baseURL, params,
// headers, signal, onUploadProgress, onDownloadProgress, cancelToken, and any other valid axios
// field). For the legacy form, a fetch-style `{ body }` (JSON string or value) is normalized into
// canonical `data`. No transport logic lives here — the canonical client owns it all.
function compatRequest(arg1, url, options) {
  // Standard axios form: request(config) — delegate unchanged, no reinterpretation.
  if (arg1 && typeof arg1 === 'object') {
    return canonicalApiClient.request(arg1)
  }
  // Legacy form: request(method, url, options).
  const opts = options || {}
  const { data, body, ...rest } = opts
  let payload = data
  if (payload === undefined && body !== undefined) {
    if (typeof body === 'string') {
      try { payload = JSON.parse(body) } catch { payload = body }
    } else {
      payload = body
    }
  }
  return canonicalApiClient.request({ method: String(arg1 || 'GET'), url, data: payload, ...rest })
}

// Proxy so every axios method/property still resolves to the canonical client; only the external
// `.request` entry point is wrapped to accept both signatures (axios internals keep using the
// canonical client's own `request`, because verb methods are bound to the canonical instance).
const apiClient = new Proxy(canonicalApiClient, {
  get(target, prop, receiver) {
    if (prop === 'request') return compatRequest
    const value = Reflect.get(target, prop, receiver)
    return typeof value === 'function' ? value.bind(target) : value
  }
})

export { apiClient }
export default apiClient
